import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Row, 
  Col, 
  Empty, 
  Modal, 
  message, 
  Spin,
  Typography,
  Divider
} from 'antd';
import CardDetailsForm from './CardDetailsForm';
import CardDisplay from './CardDisplay';
import Api from '../../helpers/apiHandler';

const { Title, Text } = Typography;
const api = new Api();

const CardManagement = (props) => {
  const { 
    userId, 
    companyId, 
    onCardChange,
    showAddButton = true,
    autoOpenAddModal = false,
    readOnly = false // employees: view cards only (billing changes are admin-only)
  } = props;
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCards();
  }, [userId, companyId]);

  useEffect(() => {
    if (autoOpenAddModal && !readOnly && cards.length === 0 && !loading) {
      setAddModalVisible(true);
    }
  }, [autoOpenAddModal, readOnly, cards, loading]);

  const fetchCards = async () => {
    if (!currentUserId) {
      console.error('No userId available for fetching cards');
      message.error('User not authenticated');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.get(`cards/user/${currentUserId}`);
      if (response.status === 1) {
        setCards(response.data || []);
        onCardChange && onCardChange(response.data || []);
      } else {
        message.error(response.message || 'Failed to fetch cards');
      }
    } catch (error) {
      console.error('Error fetching cards:', error);
      message.error('Failed to fetch cards');
    } finally {
      setLoading(false);
    }
  };

  const currentUserId = userId || (props.auth && props.auth.id);
  const currentCompanyId = companyId || (props.auth && props.auth.company && props.auth.company.id);

  // cardData comes from CardDetailsForm after Stripe confirmed the card:
  // { paymentMethodId, setupIntentId, cardholderName, addressLine1, ..., billingCountry }
  const handleAddCard = async (cardData) => {
    setSubmitting(true);

    // Fortis (replaced): contactId was the Fortis contact; the backend now uses the company's Stripe customer.
    // const fortisContactId = props.auth && props.auth.contactId;
    // const companyFortisContactId = props.auth && props.auth.company && props.auth.company.fortisContactId;

    if (!currentUserId) {
      message.error('User information not available');
      setSubmitting(false);
      return;
    }

    try {
      const response = await api.post('cards/add', {
        data: {
          ...cardData,
          userId: currentUserId,
          companyId: currentCompanyId || null,
          contactId: props.auth && props.auth.contactId
        }
      });

      if (response.status === 1) {
        message.success(response.message ||'Card added successfully');
        setAddModalVisible(false);
        fetchCards();
      } else {
        message.error(response.message || 'Failed to add card');
      }
    } catch (error) {
      console.error('Error adding card:', error);
      message.error('Failed to add card');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditCard = async (cardData) => {
    setSubmitting(true);
    try {
      // For editing, you might need to implement an update endpoint
      // For now, we'll show a message
      message.info('Edit functionality will be implemented');
      setEditModalVisible(false);
    } catch (error) {
      console.error('Error editing card:', error);
      message.error('Failed to edit card');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCard = async (cardId) => {
    // Prevent deletion if this is the only card
    if (cards.length <= 1) {
      message.warning('Cannot delete the last remaining card. Please add another card first.');
      return;
    }

    const currentUserId = userId || (props.auth && props.auth.id);
    
    if (!currentUserId) {
      message.error('User not authenticated');
      return;
    }
    
    try {
      const response = await api.delete(`cards/${cardId}?userId=${currentUserId}`);
      
      if (response.status === 1) {
        message.success('Card deleted successfully');
        fetchCards();
      } else {
        message.error(response.message || 'Failed to delete card');
      }
    } catch (error) {
      console.error('Error deleting card:', error);
      // Handle specific error messages from backend
      if (error.response && error.response.data && error.response.data.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to delete card');
      }
    }
  };

  const handleSetDefault = async (cardId) => {
    const currentUserId = userId || (props.auth && props.auth.id);
    
    if (!currentUserId) {
      message.error('User not authenticated');
      return;
    }
    
    try {
      const response = await api.put(`cards/${cardId}/set-default?userId=${currentUserId}`);
      
      if (response.status === 1) {
        message.success('Default card updated successfully');
        fetchCards();
      } else {
        message.error(response.message || 'Failed to set default card');
      }
    } catch (error) {
      console.error('Error setting default card:', error);
      message.error('Failed to set default card');
    }
  };

  const handleEdit = (card) => {
    setSelectedCard(card);
    setEditModalVisible(true);
  };

  const getDefaultCard = () => {
    return cards.find(card => card.isDefault) || cards[0];
  };

  const getNonDefaultCards = () => {
    return cards.filter(card => !card.isDefault);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading cards...</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div>
          {/* <Title level={4} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            Payment Methods
          </Title>
          <Text type="secondary">
            Manage your payment methods and billing information
          </Text> */}
        </div>
        
        {showAddButton && !readOnly && (
          <Button
            type="primary"
            onClick={() => setAddModalVisible(true)}
          >
            Add Card
          </Button>
        )}
      </div>

      {cards.length === 0 ? (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No payment methods added yet"
          >
            {!readOnly && (
            <Button 
              type="primary" 
              onClick={() => {
                setAddModalVisible(true);
              }}
            >
              Add Your First Card
            </Button>
            )}
          </Empty>
        </Card>
      ) : (
        <div>
          {/* Default Card */}
          {getDefaultCard() && (
            <div style={{ marginBottom: '24px' }}>
              <Text strong style={{ marginBottom: '12px', display: 'block' }}>
                Default Payment Method
              </Text>
              <CardDisplay
                card={getDefaultCard()}
                showActions={!readOnly}
                isDefault={true}
                onEdit={handleEdit}
                onDelete={handleDeleteCard}
                onSetDefault={handleSetDefault}
                totalCards={cards.length}
              />
            </div>
          )}

          {/* Other Cards */}
          {getNonDefaultCards().length > 0 && (
            <div>
              <Text strong style={{ marginBottom: '12px', display: 'block' }}>
                Other Payment Methods
              </Text>
              <Row gutter={16}>
                {getNonDefaultCards().map((card) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={card.id}>
                    <CardDisplay
                      card={card}
                      showActions={!readOnly}
                      isDefault={false}
                      onEdit={handleEdit}
                      onDelete={handleDeleteCard}
                      onSetDefault={handleSetDefault}
                      totalCards={cards.length}
                    />
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </div>
      )}

      {/* Add Card Modal */}
      <Modal
        title="Add Payment Method"
        visible={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        <CardDetailsForm
          onSubmit={handleAddCard}
          loading={submitting}
          showBillingAddress={true}
          userId={currentUserId}
          companyId={currentCompanyId}
        />
      </Modal>

      {/* Edit Card Modal */}
      <Modal
        title="Edit Payment Method"
        visible={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={600}
        destroyOnClose
      >
        <CardDetailsForm
          onSubmit={handleEditCard}
          loading={submitting}
          initialValues={selectedCard}
          isEdit={true}
          showBillingAddress={true}
        />
      </Modal>
    </div>
  );
};

export default CardManagement;